import { useEffect, useRef, useState } from 'react';

import { Modal } from '@/components/Modal';
import type { NoticePost } from '@/lib/types';
import { NoticeArticleBody } from '@/pages/news/NoticeArticleBody';
import { FOREVER, isDismissed, pruneDismissals, rememberDismissal, todayKey } from '@/pages/home/noticePopupDismissals';
import { NoticePopupListItems } from '@/pages/home/NoticePopupLayout';
import { articleOf, newestFirst } from '@/pages/home/noticePopupPosts';
import { openNoticeListWindow, openNoticeWindow } from '@/pages/home/noticePopupOpener';
import { useNoticePopups } from '@/pages/home/useNoticePopups';

/**
 * 홈에 들어온 사람 앞에 공지를 세운다 — 한 건이면 <b>새 창으로</b> (사용자 지시 2026-09-10).
 *
 * <h2>왜 위젯이 아니라 창인가</h2>
 * 공지사항 위젯은 이미 홈에 있다. 그런데 그 칸은 <b>다섯 줄짜리 목록</b>이라, 안 보고 지나가는
 * 것이 아무렇지 않다. 팝업은 그 반대편이다 — 글쓴이가 「이건 읽고 지나가야 한다」 고 판단한
 * 공지에만 켜고({@code popupDays}), 켜면 앞을 막고 선다.
 *
 * <h2>새 창이 먼저다 (사용자 결정 2026-09-10)</h2>
 * 다른 회사 시스템들이 그렇게 한다 — 공지 팝업은 <b>따로 떠 있는 창</b>이고, 본문을 보면서
 * 일하던 화면으로 돌아갈 수 있다. 차단은 사람이 한 번 풀면 되는 문제다.
 *
 * <h2>여럿이면 창 하나에 목록으로 (사용자 지시 2026-11-02)</h2>
 * 간만에 들어온 사람 앞에는 창이 여럿 겹쳐 뜬다. 그래서 세울 글이 둘 이상이면 <b>목록 창 하나</b>
 * 만 연다({@code openNoticeListWindow}) — 줄을 누르면 그 창 안에서 글을 보고, 「← 목록」 으로
 * 돌아온다. 「오늘/더이상 보지 않기」 는 <b>그 글 하나</b>의 것이다.
 *
 * <h2>막히면 <b>화면 안에</b> 세운다</h2>
 * 다만 차단을 풀기 <b>전에</b> 등록된 공지가 조용히 사라지면 안 된다 — 올린 사람은 나갔다고
 * 믿고, 받는 사람은 뜬 적이 없다. 그래서 {@code window.open} 이 null 로 돌아오면 종전처럼
 * 모달로 세운다 — 한 건이면 그 글을, 여럿이면 같은 목록을. 사람이 차단을 풀면 저절로 새 창
 * 쪽으로 돌아간다.
 *
 * <h2>홈에서만 뜬다</h2>
 * 다른 화면에서도 띄우면 <b>일하는 중에 끼어드는 창</b>이 된다({@code PollPrompt} 와 같은 자리).
 */
export function NoticePopup() {
  const popups = useNoticePopups();

  /** 새 창이 막혀 <b>화면 안에</b> 세워야 하는 글. */
  const [blocked, setBlocked] = useState<string[]>([]);
  /** 둘 이상인데 목록 창이 막혀 <b>목록 모달</b>로 세우는 글. */
  const [grouped, setGrouped] = useState<string[]>([]);
  /** 목록 모달 전체를 ✕·닫기로 닫았는가. 저장하지 않는다 — 새로고침하면 다시 선다. */
  const [groupClosed, setGroupClosed] = useState(false);
  /** 목록에서 지금 펼쳐 본 글. */
  const [selected, setSelected] = useState<string | null>(null);
  /** 이번에 펼쳐 본 글 — 목록에서 흐리게 둔다. */
  const [read, setRead] = useState<string[]>([]);
  /** 이번에 ✕·닫기로 닫은 것. 저장하지 않으므로 새로고침하면 다시 선다 — 그것이 닫기의 뜻이다. */
  const [closedNow, setClosedNow] = useState<string[]>([]);
  /** 이번에 「보지 않기」 를 누른 것 — 기억은 localStorage 에 있고, 이건 다시 그리게 하는 몫이다. */
  const [dismissedNow, setDismissedNow] = useState<string[]>([]);
  /** 이미 열어 본 글. 다시 그려질 때마다 창을 또 열지 않게 한다. */
  const tried = useRef<Set<string>>(new Set());

  const list = popups.data ?? [];

  /*
    안 쓰는 기억을 지운다. 기간이 지난 글은 이 목록에서 빠지므로, 목록에 없는 id 의 「더이상
    보지 않기」 도 함께 버린다 — 안 그러면 이 칸이 몇 년치 id 를 들고 자란다.
  */
  useEffect(() => {
    if (popups.data) pruneDismissals(popups.data.map((post) => post.id));
  }, [popups.data]);

  /*
    세울 글이 둘 이상이면 목록 창 하나를 연다(막히면 목록 모달). 한 건이면 종전처럼 그 글의 창을 연다.

    「안 보겠다」 고 한 것은 애초에 열지 않는다. 열고 나서 닫는 것이 아니라 <b>열지 않는 것</b>이라야
    화면이 깜빡이지 않는다.
   */
  useEffect(() => {
    if (!popups.data) return;

    const fresh = popups.data.filter((post) => !isDismissed(post.id) && !tried.current.has(post.id));
    for (const post of fresh) tried.current.add(post.id);

    if (fresh.length >= 2) {
      if (openNoticeListWindow()) return;
      setGrouped((prev) => [...prev, ...fresh.map((post) => post.id).filter((id) => !prev.includes(id))]);
      setGroupClosed(false);
      return;
    }

    for (const post of fresh) {
      if (!openNoticeWindow(post.id, 0)) {
        setBlocked((prev) => (prev.includes(post.id) ? prev : [...prev, post.id]));
      }
    }
  }, [popups.data]);

  const alive = (post: NoticePost) => !isDismissed(post.id) && !dismissedNow.includes(post.id);

  const dismissOne = (id: string, until: string) => {
    rememberDismissal(id, until);
    setDismissedNow((prev) => [...prev, id]);
    setSelected(null);
  };

  /* 목록 창이 막혔을 때의 목록 모달. 남은 글이 하나면 목록 없이 그 글을 바로 세운다. */
  const groupItems = groupClosed ? [] : newestFirst(list.filter((post) => grouped.includes(post.id) && alive(post)));

  if (groupItems.length > 0) {
    const closeGroup = () => setGroupClosed(true);
    const current =
      groupItems.find((post) => post.id === selected) ?? (groupItems.length === 1 ? groupItems[0] : null);

    if (current) {
      return (
        <NoticeArticleModal
          post={current}
          onBack={groupItems.length > 1 ? () => setSelected(null) : undefined}
          onClose={closeGroup}
          onDismiss={(until) => dismissOne(current.id, until)}
        />
      );
    }

    const dismissAllToday = () => {
      for (const post of groupItems) rememberDismissal(post.id, todayKey());
      setDismissedNow((prev) => [...prev, ...groupItems.map((post) => post.id)]);
    };

    return (
      <Modal title={`공지사항 ${groupItems.length}건`} maxWidth="560px" onClose={closeGroup}>
        <NoticePopupListItems
          posts={groupItems}
          read={read}
          onPick={(post) => {
            setSelected(post.id);
            setRead((prev) => (prev.includes(post.id) ? prev : [...prev, post.id]));
          }}
        />

        <div className="modal-popup-actions">
          <button className="btn-ghost" style={{ fontSize: '12px' }} onClick={dismissAllToday}>
            모두 오늘 보지 않기
          </button>
          <button className="btn-primary ml-auto" style={{ fontSize: '13px', padding: '5px 16px' }} onClick={closeGroup}>
            닫기
          </button>
        </div>
      </Modal>
    );
  }

  /*
    막힌 것만, 한 번에 하나씩 화면 안에 세운다. 모달 여럿을 겹쳐 세우면 뒤의 것은 열린 줄도
    모르고, 그건 창이 여럿 뜨는 것과 전혀 다른 경험이다.
   */
  const standing = list.find((post) => blocked.includes(post.id) && !closedNow.includes(post.id) && alive(post));
  if (!standing) return null;

  return (
    <NoticeArticleModal
      post={standing}
      onClose={() => setClosedNow((prev) => [...prev, standing.id])}
      onDismiss={(until) => dismissOne(standing.id, until)}
    />
  );
}

/**
 * 공지 한 건을 모달로 — 막힌 한 건과 목록에서 펼친 글이 같은 모양이다.
 *
 * ★ 버튼을 `footer` 로 주지 않는다 — 그러면 <b>제목 줄로 올라간다</b>(`Modal` 이 자리를 그리로
 *   모은다). 이 창은 입력 판이 아니라 <b>읽고 닫는 공지 팝업</b>이고, 그 모양에서 「오늘 보지
 *   않기」 는 예나 지금이나 <b>아래 왼쪽</b>이다(사용자 지시 2026-09-10). 새 창 쪽과 같은 자리라야
 *   차단을 푸는 순간 모양이 달라지지 않는다. 바탕·줄을 두지 않는 것도 같은 이유다.
 */
function NoticeArticleModal({
  post,
  onBack,
  onClose,
  onDismiss,
}: {
  post: NoticePost;
  /** 목록 모달에서 펼친 글이면 「← 목록」 이 선다. */
  onBack?: () => void;
  onClose: () => void;
  onDismiss: (until: string) => void;
}) {
  return (
    <Modal title={post.title} maxWidth="560px" onClose={onClose}>
      {onBack && (
        <button className="btn-ghost" style={{ fontSize: '12px', alignSelf: 'flex-start' }} onClick={onBack}>
          ← 목록
        </button>
      )}

      <NoticeArticleBody
        article={articleOf(post)}
        // 새 창 쪽과 같은 모양이라야 차단을 푸는 순간 화면이 달라지지 않는다 — 분류 뱃지는 빼고
        // 날짜는 글 끝 오른쪽에(`NoticePopupWindow`).
        showCategory={false}
        openLinksInNewTab
      />

      <p className="text-right" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
        {new Date(post.createdAt).toLocaleDateString('ko-KR')}
      </p>

      <div className="modal-popup-actions">
        <button className="btn-ghost" style={{ fontSize: '12px' }} onClick={() => onDismiss(todayKey())}>
          오늘 보지 않기
        </button>
        <button className="btn-ghost" style={{ fontSize: '12px' }} onClick={() => onDismiss(FOREVER)}>
          더이상 보지 않기
        </button>
        <button className="btn-primary ml-auto" style={{ fontSize: '13px', padding: '5px 16px' }} onClick={onClose}>
          닫기
        </button>
      </div>
    </Modal>
  );
}
